import React, { useEffect, useState } from "react";
import {
  Card, Button, List, Tag, Space, Typography,
  Drawer, Form, Input, Select, Row, Col, Divider, Empty,
} from "antd";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { formatDate } from "../../components/Customs/dateFormatter";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { GeneralService } from "../../Services/GeneralService";
import { notify } from "../../components/Customs/NotifyProvider";
import TitleHead from "../../components/Customs/TitleHead";


import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from "../../../utils/permissionUtils";

const MODULE_KEY = "policies";

const { Option } = Select;
const { Text } = Typography;

const Policies = () => {
  const [form] = Form.useForm();

  const [policies, setPolicies] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [description, setDescription] = useState("");

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["link", "image", "video"],
      ["clean"],
    ],
  };

  const openDrawer = () => {
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    form.resetFields();
    setDescription("");
    setEditingIndex(null);
  };

  const getPolicies = async () => {
    try {
      const req = await GeneralService.get("policies");
      setPolicies(req.data);
      console.log("req.data", req.data);

    } catch (error) {
      console.error(error);

    }
  }

  useEffect(() => {
    getPolicies();
  }, [])

  const handleSubmit = async (values) => {
    try {
      if (!description || description === "<p><br></p>") {
        return notify.error("Description is required");
      }

      const payload = {
        ...values,
        description,
        added_on: formatDate(),
      };

      let response;

      // ✅ UPDATE
      if (editingIndex !== null) {
        const currentItem = policies[editingIndex];

        response = await GeneralService.update(
          "policies",
          currentItem.id, // ✅ correct ID
          payload
        );

        if (response.status) {
          notify.success("Success", response.message);
        } else {
          notify.error("Error", response.message);
        }

        getPolicies();

      }
      // ✅ CREATE
      else {
        response = await GeneralService.create("policies", payload);

        if (response.status) {
          notify.success("Success", response.message);
        } else {
          notify.error("Error", response.message);
        }

        getPolicies();
      }

      console.log("API Response:", response);

      closeDrawer();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleEdit = (item, index) => {
    form.setFieldsValue({
      policy_name: item.policy_name,
      status: item.status,
    });
    setDescription(item.description);
    setEditingIndex(index);
    setDrawerOpen(true);
  };

  const handleDelete = async (item, index) => {
    console.log(item?.id);
    try {
      const req = await GeneralService.delete("policies", item?.id);
      console.log(req);

      if (req.status) {
        notify.success("Success", req.message);
      } else {
        notify.error("Error", req.message);
      }

      getPolicies();

    } catch (error) {
      console.error(error);

    }
  };
    if (!canViewModule(MODULE_KEY)) {
    return <Empty description="No permission to view this page" />;
  }

  return (
    <>
      {/* Header */}
      <Card
        title={
          <TitleHead h1='Manage Policies' h2='Policy settings, rules configuration & compliance management overview' />
        }
   extra={
    canCreateModule(MODULE_KEY) && (
        <Button size="large" type="primary" onClick={openDrawer}>
            Add Policy
        </Button>
    )
}
      >
        <List
          dataSource={policies}
          locale={{ emptyText: "No policies added" }}
          renderItem={(item, index) => (
            <List.Item
              actions={[
                <Space>
              {canUpdateModule(MODULE_KEY) && (
    <Button
        type="text"
        icon={<EditOutlined style={{ fontSize: 20 }} />}
        onClick={() => handleEdit(item, index)}
    />
)}
              {canDeleteModule(MODULE_KEY) && (
    <Button
        type="text"
        danger
        icon={<DeleteOutlined style={{ fontSize: 20 }} />}
        onClick={() => handleDelete(item, index)}
    />
)}
                </Space>
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{item.policy_name}</Text>
                    <Tag color={item.status === "active" ? "green" : "orange"}>
                      {item.status}
                    </Tag>
                  </Space>
                }
                description={
                  <div>
                    <div
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'right' }}>
                      <Text type="secondary">
                        Added on: {item.added_on}
                      </Text>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      {/* Drawer Form */}
      <Drawer
        title={editingIndex !== null ? "Edit Policy" : "Add Policy"}
        width={720}
        onClose={closeDrawer}
        open={drawerOpen}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: "active" }}
        >
          {/* Name + Status */}
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="policy_name"
                label="Policy Name"
                rules={[{ required: true, message: "Enter policy name" }]}
              >
                <Input placeholder="Enter policy name" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="draft">Draft</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Rich Editor */}
          <Form.Item label="Description" required>
            <ReactQuill
              value={description}
              onChange={setDescription}
              modules={quillModules}
              style={{ height: 550, marginBottom: 50 }}
            />
          </Form.Item>

          <Divider />

          <Space>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              {editingIndex !== null ? "Update" : "Submit"}
            </Button>
          </Space>
        </Form>
      </Drawer>
    </>
  );
};

export default Policies;